import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { DEFAULT_LOGIN_APPEARANCE, mergeAppearance } from '../utils/loginAppearance';
import { SettingsIcon, ImageIcon, TrashIcon, PlusIcon, ChevronUpIcon, ChevronDownIcon } from '../components/Icon';
import { can } from '../utils/permissions';
import { getCurrentUser } from '../utils/auth';
import { confirmDialog } from '../utils/dialog';

export default function Settings() {
  const canEdit = can(getCurrentUser(), 'settings.edit');
  const [form, setForm] = useState(DEFAULT_LOGIN_APPEARANCE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null); // { type, msg }
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    let alive = true;
    api.settings().then(res => {
      if (!alive) return;
      setForm(res?.value ? mergeAppearance(res.value) : { ...DEFAULT_LOGIN_APPEARANCE });
      setLoading(false);
    }).catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const set = (key, value) => setForm(f => ({ ...f, [key]: value }));

  // ── Image list helpers ──
  const images = form.images || [];
  const setImage = (i, v) => set('images', images.map((s, idx) => idx === i ? v : s));
  const addImage = () => set('images', [...images, '']);
  const removeImage = (i) => set('images', images.filter((_, idx) => idx !== i));
  const moveImage = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= images.length) return;
    const next = images.slice();
    [next[i], next[j]] = [next[j], next[i]];
    set('images', next);
  };

  const save = async () => {
    setSaving(true); setToast(null);
    // Persist a cleaned/merged copy so the saved payload is always valid.
    const payload = mergeAppearance(form);
    try {
      const res = await api.settingsSave(payload);
      if (res?.success) {
        setForm(mergeAppearance(res.value));
        setToast({ type: 'ok', msg: 'Login appearance saved. Changes are live.' });
      } else {
        setToast({ type: 'err', msg: res?.error || 'Save failed' });
      }
    } catch {
      setToast({ type: 'err', msg: 'Network error. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = async () => {
    const ok = await confirmDialog({
      title: 'Reset Settings',
      message: 'Reset all login appearance settings to the original defaults?\nYou still need to click Save to apply.',
      confirmText: 'Reset',
    });
    if (ok) {
      setForm({ ...DEFAULT_LOGIN_APPEARANCE, images: DEFAULT_LOGIN_APPEARANCE.images.slice() });
    }
  };

  const preview = mergeAppearance(form);
  const previewImg = preview.images[Math.min(slide, preview.images.length - 1)] || '';

  if (loading) return <div className="set-loading">Loading settings…</div>;

  return (
    <div className="page">
      <header className="set-header">
        <div className="set-head-icon"><SettingsIcon size={20} /></div>
        <div>
          <h1 className="set-title">Login Page Settings</h1>
          <p className="set-subtitle">Everything on the login screen is configurable here. Changes apply to all users after saving.</p>
        </div>
        <div className="set-spacer" />
        {canEdit && (
          <>
            <button className="set-reset-btn" onClick={resetDefaults} disabled={saving}>Reset to defaults</button>
            <button className={`set-save-btn${saving ? ' set-btn-disabled' : ''}`} onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </>
        )}
      </header>

      {toast && (
        <div className={`set-toast ${toast.type === 'ok' ? 'set-toast--ok' : 'set-toast--err'}`}>{toast.msg}</div>
      )}

      <div className="set-layout">
        {/* ── Editor ── */}
        <div className="set-editor">
          <Section title="Branding">
            <Field label="Brand name">
              <input className="set-input" value={form.brandName} onChange={e => set('brandName', e.target.value)} />
            </Field>
            <Field label="Logo URL" hint="Shown as a badge over the showcase image. Leave blank to show the brand name as text.">
              <input className="set-input" value={form.logoUrl} onChange={e => set('logoUrl', e.target.value)} placeholder="https://…" />
            </Field>
            <Field label="Tagline" hint="Small uppercase line above the heading.">
              <input className="set-input" value={form.tagline} onChange={e => set('tagline', e.target.value)} />
            </Field>
          </Section>

          <Section title="Form text">
            <Field label="Heading">
              <input className="set-input" value={form.heading} onChange={e => set('heading', e.target.value)} />
            </Field>
            <Field label="Sub-heading">
              <input className="set-input" value={form.subheading} onChange={e => set('subheading', e.target.value)} />
            </Field>
            <div className="set-row2">
              <Field label="Username label">
                <input className="set-input" value={form.usernameLabel} onChange={e => set('usernameLabel', e.target.value)} />
              </Field>
              <Field label="Password label">
                <input className="set-input" value={form.passwordLabel} onChange={e => set('passwordLabel', e.target.value)} />
              </Field>
            </div>
            <Field label="Button text">
              <input className="set-input" value={form.buttonText} onChange={e => set('buttonText', e.target.value)} />
            </Field>
            <Field label="Footer text">
              <input className="set-input" value={form.footerText} onChange={e => set('footerText', e.target.value)} />
            </Field>
          </Section>

          <Section title="Options">
            <label className="set-check">
              <input type="checkbox" checked={!!form.showRememberMe} onChange={e => set('showRememberMe', e.target.checked)} />
              Show “Remember Me” checkbox
            </label>
            <label className="set-check">
              <input type="checkbox" checked={!!form.showForgotPassword} onChange={e => set('showForgotPassword', e.target.checked)} />
              Show “Forgot Password” link
            </label>
            {form.showForgotPassword && (
              <div className="set-row2">
                <Field label="Forgot-password text">
                  <input className="set-input" value={form.forgotPasswordText} onChange={e => set('forgotPasswordText', e.target.value)} />
                </Field>
                <Field label="Forgot-password URL" hint="Optional. Leave blank for a non-linked label.">
                  <input className="set-input" value={form.forgotPasswordUrl} onChange={e => set('forgotPasswordUrl', e.target.value)} placeholder="https://…" />
                </Field>
              </div>
            )}
          </Section>

          <Section title="Showcase panel">
            <Field label="Panel title">
              <input className="set-input" value={form.panelTitle} onChange={e => set('panelTitle', e.target.value)} />
            </Field>
            <Field label="Panel subtitle">
              <textarea className="set-input set-textarea" value={form.panelSubtitle} onChange={e => set('panelSubtitle', e.target.value)} />
            </Field>

            <div className="set-images-head">
              <span className="set-field-label">Background images <span className="set-hint">(rotating carousel)</span></span>
              <button className="set-add-btn" onClick={addImage} type="button"><PlusIcon size={13} /> Add image</button>
            </div>
            {images.length === 0 && <div className="set-images-empty">No images yet — defaults will be used. Add one above.</div>}
            {images.map((src, i) => (
              <div key={i} className="set-image-row">
                <div className="set-thumb">
                  {src
                    ? <img src={src} alt="" className="set-thumb-img" onError={e => { e.target.style.visibility = 'hidden'; }} />
                    : <ImageIcon size={16} />}
                </div>
                <input className="set-input set-flex1" value={src} onChange={e => setImage(i, e.target.value)} placeholder="https://… image URL" />
                <div className="set-image-btns">
                  <button type="button" className="set-icon-btn" onClick={() => moveImage(i, -1)} disabled={i === 0} title="Move up"><ChevronUpIcon size={14} /></button>
                  <button type="button" className="set-icon-btn" onClick={() => moveImage(i, 1)} disabled={i === images.length - 1} title="Move down"><ChevronDownIcon size={14} /></button>
                  <button type="button" className="set-icon-btn set-icon-btn--del" onClick={() => removeImage(i)} title="Remove"><TrashIcon size={14} /></button>
                </div>
              </div>
            ))}

            <div className="set-row2">
              <Field label="Carousel interval (seconds)" hint="0 = don't auto-advance.">
                <input className="set-input" type="number" min="0" value={form.carouselInterval} onChange={e => set('carouselInterval', e.target.value)} />
              </Field>
              <Field label={`Image darkening (${Math.round(preview.panelOverlay * 100)}%)`} hint="Higher = darker, for readable text.">
                <input className="set-range" type="range" min="0" max="1" step="0.05" value={form.panelOverlay} onChange={e => set('panelOverlay', e.target.value)} />
              </Field>
            </div>
          </Section>

          <Section title="Theme">
            <div className="set-row2">
              <Field label="Primary colour" hint="Buttons, links, accents.">
                <ColorInput value={form.primaryColor} onChange={v => set('primaryColor', v)} />
              </Field>
              <Field label="Primary colour (gradient end)">
                <ColorInput value={form.primaryColorDark} onChange={v => set('primaryColorDark', v)} />
              </Field>
            </div>
          </Section>
        </div>

        {/* ── Live preview ── (theme colours/images are runtime values → inline) */}
        <div className="set-preview-col">
          <div className="set-preview-label">Live preview</div>
          <div className="set-preview-card">
            <div className="set-pv-left">
              {previewImg && <div className="set-pv-img" style={{ backgroundImage: `url(${previewImg})` }} />}
              <div className="set-pv-overlay" style={{ background: `linear-gradient(180deg, rgba(0,0,0,${preview.panelOverlay * 0.5}), rgba(0,0,0,${Math.min(1, preview.panelOverlay + 0.2)}))` }} />
              <div className="set-pv-brand">
                {form.logoUrl
                  ? <img src={form.logoUrl} alt="" className="set-pv-logo" onError={e => { e.target.style.display = 'none'; }} />
                  : <span className="set-pv-brand-text">{form.brandName}</span>}
              </div>
              <div className="set-pv-left-text">
                <div className="set-pv-panel-title">{preview.panelTitle}</div>
                <div className="set-pv-panel-sub">{preview.panelSubtitle}</div>
                {preview.images.length > 1 && (
                  <div className="set-pv-dots">
                    {preview.images.map((_, i) => (
                      <button key={i} type="button" onClick={() => setSlide(i)}
                        className={`set-pv-dot${i === Math.min(slide, preview.images.length - 1) ? ' is-active' : ''}`} />
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="set-pv-right">
              {preview.tagline && <div className="set-pv-tagline" style={{ color: preview.primaryColor }}>{preview.tagline}</div>}
              <div className="set-pv-heading">{preview.heading}</div>
              <div className="set-pv-sub">{preview.subheading}</div>
              <div className="set-pv-field-label">{preview.usernameLabel}</div>
              <div className="set-pv-input" />
              <div className="set-pv-field-label">{preview.passwordLabel}</div>
              <div className="set-pv-input" />
              {(preview.showRememberMe || preview.showForgotPassword) && (
                <div className="set-pv-options">
                  {preview.showRememberMe ? <span>☑ Remember Me</span> : <span />}
                  {preview.showForgotPassword && <span style={{ color: preview.primaryColor }}>{preview.forgotPasswordText}</span>}
                </div>
              )}
              <div className="set-pv-btn" style={{ background: `linear-gradient(135deg, ${preview.primaryColor}, ${preview.primaryColorDark})` }}>
                {preview.buttonText}
              </div>
              {preview.footerText && <div className="set-pv-footer">{preview.footerText}</div>}
            </div>
          </div>
          <p className="set-preview-note">This is an approximation. Open the login page in a private window to see the final result.</p>
        </div>
      </div>
    </div>
  );
}

// ── Small building blocks ──
function Section({ title, children }) {
  return (
    <section className="set-section">
      <h3 className="set-section-title">{title}</h3>
      {children}
    </section>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="set-field">
      <label className="set-field-label">{label}{hint && <span className="set-hint"> — {hint}</span>}</label>
      {children}
    </div>
  );
}

function ColorInput({ value, onChange }) {
  return (
    <div className="set-color-row">
      <input type="color" value={value} onChange={e => onChange(e.target.value)} className="set-color-swatch" />
      <input className="set-input set-flex1" value={value} onChange={e => onChange(e.target.value)} />
    </div>
  );
}

